import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Alert, Button, Snackbar, Stack, Tooltip, Typography } from '@mui/material';
import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';

/**
 * Gets an operator into a portal without ever putting the password on screen.
 *
 * Copy-to-clipboard, not reveal. The whole reason this project exists is that the Pro Billing
 * Tracker keeps portal passwords in plaintext cells anyone with the sheet can read; rendering them
 * here would move that exposure rather than remove it. Every copy still decrypts exactly one
 * credential server-side and writes an audit row, so the access is traceable either way - the
 * difference is that a shoulder, a screen share, or a screenshot cannot capture what is never drawn.
 */
export const CredentialAccess = observer(_ref => {
  let item = _ref.item,
    onOpenPortal = _ref.onOpenPortal;
  const store = useStore();
  const _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    busy = _useState2[0],
    setBusy = _useState2[1];
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    toast = _useState4[0],
    setToast = _useState4[1];
  const _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    error = _useState6[0],
    setError = _useState6[1];
  const copy = async function (which) {
    let portalToo = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    if (!(item !== null && item !== void 0 && item.credentialId)) return;
    setBusy(which);
    setError('');
    // Opened on the gesture, before any await: a popup blocker kills window.open once the reveal
    // round-trip has expired the user activation, so the primary action copied the password and
    // never opened the portal. The tab is closed again if the reveal fails.
    const portal = portalToo ? onOpenPortal === null || onOpenPortal === void 0 ? void 0 : onOpenPortal(item.providerWebsite) : null;
    try {
      const resp = await store.admin.revealBillQueueCredentialAsync.run(item.credentialId);
      const secret = (resp === null || resp === void 0 ? void 0 : resp.response) || resp;
      const value = which === 'password' ? secret === null || secret === void 0 ? void 0 : secret.password : secret === null || secret === void 0 ? void 0 : secret.username;
      if (!value) {
        var _portal$close;
        portal === null || portal === void 0 || (_portal$close = portal.close) === null || _portal$close === void 0 || _portal$close.call(portal);
        setError('The credential could not be decrypted.');
        return;
      }
      await navigator.clipboard.writeText(value);
      setToast("".concat(which === 'password' ? 'Password' : 'Username', " copied. It was not displayed."));
      if (portal && secret !== null && secret !== void 0 && secret.portalUrl && secret.portalUrl !== item.providerWebsite) {
        portal.location = secret.portalUrl;
      }
    } catch (ex) {
      var _portal$close2;
      portal === null || portal === void 0 || (_portal$close2 = portal.close) === null || _portal$close2 === void 0 || _portal$close2.call(portal);
      setError('Could not copy the credential.');
    } finally {
      setBusy('');
    }
  };
  if (!(item !== null && item !== void 0 && item.credentialId)) {
    return /*#__PURE__*/React.createElement(Alert, {
      severity: "info",
      sx: {
        marginTop: '8px'
      }
    }, "No credential is stored for this utility. Add one on the property's utility so the scraper can use it too.");
  }
  return /*#__PURE__*/React.createElement(Stack, {
    spacing: 1,
    sx: {
      marginTop: '8px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, "Signed in as ".concat(item.usernameHint || 'a stored login', ". Passwords are copied to the clipboard, never shown.")), error ? /*#__PURE__*/React.createElement(Alert, {
    severity: "error"
  }, error) : null, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 1,
    flexWrap: "wrap",
    useFlexGap: true
  }, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "contained",
    disabled: !!busy,
    onClick: () => copy('password', true)
  }, "Open portal + copy password"), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "actionButton",
    disabled: !!busy,
    onClick: () => copy('username')
  }, "Copy username"), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "actionButton",
    disabled: !!busy,
    onClick: () => copy('password')
  }, "Copy password"), item.accountNumber ? /*#__PURE__*/React.createElement(Tooltip, {
    title: "Portals are matched on account number, not address."
  }, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "actionButtonInvert",
    onClick: async () => {
      try {
        await navigator.clipboard.writeText(item.accountNumber);
        setToast('Account number copied.');
      } catch (ex) {
        setError('Could not copy the account number.');
      }
    }
  }, "Copy account ".concat(item.accountNumber))) : null), item.instructions ? /*#__PURE__*/React.createElement(Alert, {
    severity: "info"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, item.instructions)) : null, /*#__PURE__*/React.createElement(Snackbar, {
    open: !!toast,
    autoHideDuration: 3000,
    onClose: () => setToast(''),
    message: toast
  }));
});